import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Modal, Spinner } from './shared';

const TYPE_COLORS = {
    skills: 'bg-blue-100 text-blue-700',
    subagents: 'bg-purple-100 text-purple-700',
    memories: 'bg-green-100 text-green-700',
    mcp_servers: 'bg-orange-100 text-orange-700',
    tools: 'bg-yellow-100 text-yellow-700',
    agent_configs: 'bg-red-100 text-red-700',
    agent_instances: 'bg-gray-200 text-gray-700',
};

function TypeBadge({ type }) {
    return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-600'}`}>
            {type}
        </span>
    );
}

function JsonView({ data, depth = 0 }) {
    if (data === null || data === undefined) return <span className="text-gray-400">null</span>;
    if (typeof data === 'boolean') return <span className="text-purple-600">{String(data)}</span>;
    if (typeof data === 'number') return <span className="text-blue-600">{data}</span>;
    if (typeof data === 'string') {
        if (data.length > 300) return <span className="text-green-700 break-all">"{data.slice(0, 300)}..."</span>;
        return <span className="text-green-700 break-all">"{data}"</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="text-gray-400">[]</span>;
        return (
            <div className={depth > 0 ? 'ml-4' : ''}>
                <span className="text-gray-500">[</span>
                {data.slice(0, 50).map((item, i) => (
                    <div key={i} className="ml-4">
                        <JsonView data={item} depth={depth + 1} />
                        {i < Math.min(data.length, 50) - 1 && <span className="text-gray-400">,</span>}
                    </div>
                ))}
                {data.length > 50 && <div className="ml-4 text-gray-400">... ({data.length - 50} more)</div>}
                <span className="text-gray-500">]</span>
            </div>
        );
    }

    if (typeof data === 'object') {
        const entries = Object.entries(data);
        if (entries.length === 0) return <span className="text-gray-400">{'{}'}</span>;
        return (
            <div className={depth > 0 ? 'ml-4' : ''}>
                <span className="text-gray-500">{'{'}</span>
                {entries.slice(0, 50).map(([k, v], i) => (
                    <div key={k} className="ml-4">
                        <span className="text-red-600">"{k}"</span>
                        <span className="text-gray-500">: </span>
                        <JsonView data={v} depth={depth + 1} />
                        {i < Math.min(entries.length, 50) - 1 && <span className="text-gray-400">,</span>}
                    </div>
                ))}
                {entries.length > 50 && <div className="ml-4 text-gray-400">... ({entries.length - 50} more)</div>}
                <span className="text-gray-500">{'}'}</span>
            </div>
        );
    }

    return <span>{String(data)}</span>;
}

function ItemDetailModal({ item, open, onClose }) {
    if (!item) return null;

    return (
        <Modal open={open} onClose={onClose} title={`${item._type} / ${item.id}`} size="xl">
            <Modal.Body>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-auto max-h-[60vh]">
                    <pre className="text-xs font-mono leading-relaxed whitespace-pre-wrap">
                        <JsonView data={item} />
                    </pre>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default function AdminLocalCache() {
    const [cacheTypes, setCacheTypes] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [filterType, setFilterType] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [total, setTotal] = useState(0);
    const [availablePids, setAvailablePids] = useState([]);
    const [selectedPid, setSelectedPid] = useState(null);

    const fetchCache = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedPid) params.pid = selectedPid;
            if (filterType) params.cache_type = filterType;
            if (search) params.search = search;
            const res = await adminApi.listLocalCache(params);
            setCacheTypes(res.cache_types || {});
            setTotal(res.total || 0);
            setAvailablePids(res.available_pids || []);
            if (!selectedPid && res.pid) {
                setSelectedPid(res.pid);
            }
        } catch (e) {
            console.error('Failed to fetch local cache:', e);
        } finally {
            setLoading(false);
        }
    }, [selectedPid, filterType, search]);

    useEffect(() => {
        fetchCache();
    }, [fetchCache]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const allTypes = Object.keys(cacheTypes);
    const allItems = [];
    for (const [type, items] of Object.entries(cacheTypes)) {
        for (const item of items) {
            allItems.push({ ...item, _type: type });
        }
    }

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Local Cache Browser</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} items in cache
                        {selectedPid && <span> &middot; PID {selectedPid}</span>}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {availablePids.length > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Worker:</span>
                            <div className="flex items-center gap-1">
                                {availablePids.map(pid => (
                                    <button
                                        key={pid}
                                        onClick={() => setSelectedPid(pid)}
                                        className={`px-2.5 py-1 text-xs font-mono rounded-md border transition-colors ${
                                            selectedPid === pid
                                                ? 'bg-gray-900 text-white border-gray-900'
                                                : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        PID {pid}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={fetchCache}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                    <form onSubmit={handleSearch} className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by id, name, or description..."
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                        >
                            Search
                        </button>
                    </form>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                            onClick={() => setFilterType('')}
                            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                !filterType ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            All
                        </button>
                        {allTypes.map(type => (
                            <button
                                key={type}
                                onClick={() => setFilterType(type === filterType ? '' : type)}
                                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                    filterType === type ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {type} ({cacheTypes[type]?.length || 0})
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Spinner size="sm" text="Loading cache..." />
                        </div>
                    ) : allItems.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-sm">No items found</p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                                    <th className="px-4 py-2 font-medium">ID</th>
                                    <th className="px-4 py-2 font-medium">Name</th>
                                    <th className="px-4 py-2 font-medium w-28">Type</th>
                                    <th className="px-4 py-2 font-medium w-20 text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {allItems.map(item => (
                                    <tr
                                        key={`${item._type}-${item.id}`}
                                        onClick={() => setSelectedItem(item)}
                                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <td className="px-4 py-2.5">
                                            <span className="text-sm font-mono text-gray-700">{item.id}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="text-sm text-gray-900">{item.name !== item.id ? item.name : ''}</span>
                                            {item.description && (
                                                <p className="text-xs text-gray-400 truncate max-w-xs">{item.description}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <TypeBadge type={item._type} />
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {item.is_active !== null && item.is_active !== undefined && (
                                                <span className={`inline-block w-2 h-2 rounded-full ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <ItemDetailModal
                item={selectedItem}
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
            />
        </div>
    );
}
